/**
 * Is there a newer desk than the one running? The build writes /version.json; an open tab asks for
 * it every two minutes and whenever it comes back into view, and says so when the id differs. It
 * never reloads on its own: a rep may be mid-note or mid-call (Jeff, 2026-10-23).
 */
import { useEffect, useState } from 'react';

export const RUNNING_BUILD = typeof __DESK_BUILD_ID__ === 'string' ? __DESK_BUILD_ID__ : 'dev';
const EVERY_MS = 2 * 60_000;

export async function latestBuild(fetchImpl: typeof fetch = fetch): Promise<string | null> {
  try {
    const res = await fetchImpl(`/version.json?t=${String(Date.now())}`, { cache: 'no-store' });
    if (!res.ok) return null;
    const body = (await res.json()) as { id?: unknown };
    return typeof body.id === 'string' ? body.id : null;
  } catch {
    return null;
  }
}

export function useNewVersion(running: string = RUNNING_BUILD, fetchImpl: typeof fetch = fetch): boolean {
  const [stale, setStale] = useState(false);
  useEffect(() => {
    if (running === 'dev') return undefined;
    let stopped = false;
    const check = async () => {
      const id = await latestBuild(fetchImpl);
      if (!stopped && id !== null && id !== running) setStale(true);
    };
    const onVisible = () => {
      if (document.visibilityState === 'visible') void check();
    };
    const timer = setInterval(() => void check(), EVERY_MS);
    document.addEventListener('visibilitychange', onVisible);
    void check();
    return () => {
      stopped = true;
      clearInterval(timer);
      document.removeEventListener('visibilitychange', onVisible);
    };
  }, [running, fetchImpl]);
  return stale;
}
